import React, {
  useState,
  useCallback,
  useLayoutEffect,
  useEffect
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  EDUCATOR_PATH,
  PARENT_PATH,
  HOME_PATH,
  SIGNUP_PATH,
  SIGNIN_PATH,
  STUDENT_PATH,
  STUDENT_SIGNIN_PATH,
  PARENT_SIGNIN_PATH,
  TEACHER_SIGNIN_PATH,
} from '../../../../routes/route-paths';
import { throttle } from '../../../../utils/web-helper';
import { ThemeProvider } from 'styled-components';
import Logo from '../logo';
import EducatorMenu from '../educator-menu';
import CurriculumMenu from '../curriculum-menu';
import connect from '../../../../stores/parent-dashboard';
import { trackToGtmFE } from '../../../../utils/gtm-event-common';


// The Header component is never unmounted across different routes for better performance and faster switch
// Instead we will rerender on location change using useLayoutEffect and useEffect

const HeaderDesktop = ({ abTestsDetails, isTutoring, settings }) => {
  const [whiteState, setWhiteState] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [activePath, setActivePath] = useState(null);
  const location = useLocation();
  var scrollEventDispathced = false;
  // useCallbacks are necessary to avoid unneccessary creation of new instance of static functions
  const handleScroll = useCallback(() => {
    // white header is only shown after certain height on HOME page.
    const currentY = window.pageYOffset;
    window.innerHeight = 400;
    if ((location.pathname === "/") && !scrollEventDispathced && currentY > 500){
      scrollEventDispathced = true;
      trackEventOnGA("First Fold Scrolled",'','',{'nonInteraction': 1});
  }
    setWhiteState(() => currentY > window.innerHeight);
    setHideHeader(() => currentY >= 80 && currentY < window.innerHeight);
  }, []);
  const throttledHeader = useCallback(throttle(handleScroll, 100), [location]);
  // Don't change this to useEffect.
  // Using useLayoutEffect because to unsubscribe the scroll event on route change before the new page is loaded. new page
  // load can trigger scroll event which can set wrong state
  useLayoutEffect(() => {
    const homePageCheck = location.pathname === HOME_PATH;
    setWhiteState(() => !homePageCheck);
    // set active path so that corresponding link will be highlighted
    setActivePath(location.pathname);
    // Register scroll event only for HOME page
    window.addEventListener('scroll', throttledHeader);
    return () => {
      window.removeEventListener('scroll', throttledHeader);
    };
  }, [location]);

  // return header content based on the pathname
  const showSignupButton = window.pageYOffset > window.innerHeight;
  const HeaderContent = () => {
    switch (location.pathname) {
      case SIGNUP_PATH:
        return <ContentWithLoginOnly />;
      default:
        return (
          <ContentWithAllLinks
            activePath={activePath}
            showSignupButton={showSignupButton}
            abTestsDetails={abTestsDetails}
            isTutoring={isTutoring}
            settings={settings}
          />
        );
    }
  };
  // below is the main return. Content is dynamic based on route
  // Like signup page only has login button and no other link. Returning the content part using switch case function defined above
  // On home page we have transparent and white header. white state is passed using Themeprovider to the styled components
  return (
      <ThemeProvider theme={{ white: (location.pathname === HOME_PATH) ? whiteState : true }} >
        <div className={`${(location.pathname === HOME_PATH) && hideHeader?"header-desktop-nav-wrapper-hide":""} ${(location.pathname === HOME_PATH) && whiteState?"header-desktop-theme-true":""} ${(location.pathname !== HOME_PATH)?"header-desktop-theme-true":""} header-desktop-nav-wrapper`} hide={hideHeader}>
          <div className="content-container">
            <div className="logo-wrapper">
              <Logo />
            </div>
            {HeaderContent()}
          </div>
        </div>
      </ThemeProvider>
    );
};

const ContentWithLoginOnly = () => {
  return (
    <>
      <ul className="header-desktop-login-link-ul">
        <li className={`login-link-signup header-desktop-theme-true-login-link-signup-btn-blue}`}>
          <a href="/signin" className="header-desktop-login-link-signup-btn-blue-a">Log in</a>
        </li>
      </ul>
    </>
  );
};

const windowReload = () => {
  setTimeout(function(){
    window.location.reload();
  },10);
};

const ContentWithAllLinks = ({ activePath, isTutoring, settings }) => {
  const location = useLocation();
  const isLoginPage = (location.pathname === SIGNIN_PATH || location.pathname === STUDENT_SIGNIN_PATH || location.pathname === STUDENT_PATH || location.pathname === PARENT_SIGNIN_PATH || location.pathname === TEACHER_SIGNIN_PATH);
  const homePageCheck = location.pathname === HOME_PATH;
  const currentHeight = window.pageYOffset;
  const [hrefLink, setHrefLink] = useState('')
  const [buttonText, setButtonText] = useState('')

  let locationWhiteStateCheck;
  if ( currentHeight > window.innerHeight && location != HOME_PATH ) {
    locationWhiteStateCheck=true;
  }
  else{
    locationWhiteStateCheck=false;
  }

  useEffect(() => {
    if(settings.currentUser){
      setHrefLink('/signout')
      setButtonText('Log out')
    }
    else{
      setHrefLink('/signin')
      setButtonText('Log in')
    }
  },[settings.currentUser])

  return (
    <>
      <div className="header-desktop-nav-link-wrapper">
        <ul>
          <li className={`deep-dark ${activePath === HOME_PATH?"nav-link active":"nav-link not-active"}`} active={activePath === HOME_PATH}>
            <Link
              className="nav-link-a"
              to={HOME_PATH}
              onClick={(e) => {
                typeof trackEventOnGA != "undefined" && trackEventOnGA('Parent Link', "Clicked", 'from: Global Header' ); 
                trackToGtmFE('clicked_navigation',{'Click Text': e?.target?.innerText,'Click URL':e?.target?.href})
                windowReload();
              }}
            >
              Parents
            </Link>
          </li>
          <li className={`deep-dark ${activePath === EDUCATOR_PATH?"nav-link educator active":"educator nav-link not-active"}`} active={activePath === EDUCATOR_PATH}
            onMouseOver={() => typeof trackEventOnGA != "undefined" && trackEventOnGA('Educator Link', "Hovered", 'from: Global Header' )} data-testid="educator-link"
          >
            <span>Educators</span>
            <span className={"arrow deep-dark"}></span>
            <strong></strong>
            <EducatorMenu />
          </li>
          <li className={`deep-dark ${activePath === EDUCATOR_PATH?"nav-link educator not-active":"educator nav-link not-active"}`} active={activePath === EDUCATOR_PATH}
            onMouseOver={() => typeof trackEventOnGA != "undefined" && trackEventOnGA('Learning App Link', "Hovered", 'from: Global Header' )} data-testid="learning-app-link"
          >
            <span>Curriculum</span>
            <span className={"arrow deep-dark"}></span>
            <CurriculumMenu />
          </li>
        </ul>
      </div>
      <ul className="header-desktop-login-link-ul">
        <a href={`${hrefLink}` + ((location.pathname === PARENT_PATH) ? '?userType=parent' : '')} onClick={()=>{
          trackToGtmFE('clicked_navigation',{'Click Text': buttonText,'Click URL': hrefLink+((location.pathname === PARENT_PATH) ? '?userType=parent' : '')});
        }}>
          <ul className={`login-btn dark-theme`} homePageCheck={homePageCheck} currentHeight={currentHeight} locationWhiteStateCheck={locationWhiteStateCheck}>
          { (!isLoginPage) &&
            <li className={"home-path"}>
                {buttonText}
            </li>
          }
          </ul>
        </a>
        {!settings.currentUser &&
          <li className={`header-desktop-login-link-signup header-desktop-theme-true-login-link-signup-btn-blue ${isTutoring && (currentHeight > window.innerHeight) && "tutoring-signup-btn"}`}>
            <Link
              to={SIGNUP_PATH}
              className="header-desktop-login-link-signup-btn-blue-a"
              onClick={() => {
                trackToGtmFE('clicked_navigation',{'Click Text':'Sign Up','Click URL':SIGNUP_PATH})
                windowReload
                }
              }
            >
              Sign up
            </Link>
          </li>}
      </ul>
    </>
  );
};

function mapStateToProps(state) {
  return {
    settings: state.settings
  };
}

export default connect(mapStateToProps, null, HeaderDesktop);
